// src/components/ProjectDetail.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./ProjectDetail.css";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ProjectDetail({ items }) {
  const { slug } = useParams();
  const query = useQuery();
  const nav = useNavigate();

  const project = items.find((p) => p.slug === slug);

  const [demoOpen, setDemoOpen] = useState(false);
  const videoRef = useRef(null);


  const goBack = () => {
    // direct entry (resume link) or new tab -> go home
    if (window.history.length <= 1) {
      nav("/", { replace: true });
      return;
    }
    nav(-1);
  };



  // Open demo if ?demo=1
  useEffect(() => {
    if (query.get("demo") === "1") setDemoOpen(true);
  }, [query]);

  // Close on ESC (only when modal is open)
  useEffect(() => {
    if (!demoOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setDemoOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [demoOpen]);

  // When modal closes, pause + rewind video so it doesn't keep playing in the background
  useEffect(() => {
    if (demoOpen) return;
    const v = videoRef.current;
    if (!v) return;

    try {
      v.pause();
      v.currentTime = 0;
    } catch {
      // ignore (some browsers can throw if video not ready)
    }
  }, [demoOpen]);

  if (!project) {
    return (
      <div className="pd-wrap">
        <button className="pd-back" onClick={goBack}>
          ← Back
        </button>
        <h1 className="pd-title">Project not found</h1>
      </div>
    );
  }

  return (
    <div className="pd-wrap">
      <button className="pd-back" onClick={goBack}>
        ← Back
      </button>

      {/* Centered + stacked layout */}
      <div className="pd-header">
        <img className="pd-thumb" src={project.image} alt={project.title} />

        <h1 className="pd-title">{project.title}</h1>

        {project.description ? <p className="pd-desc">{project.description}</p> : null}

        {project.tech ? <div className="pd-tech">{project.tech}</div> : null}

        <div className="pd-actions">
          {project.demo ? (
            <button className="pd-btn" onClick={() => setDemoOpen(true)}>
              Watch Demo
            </button>
          ) : (
            <span className="pd-muted">No demo</span>
          )}

          {project.github ? (
            <a className="pd-btn ghost" href={project.github} target="_blank" rel="noreferrer noopener">
              GitHub
            </a>
          ) : (
            <span className="pd-muted">Private</span>
          )}

          {project.live ? (
            <a className="pd-btn ghost" href={project.live} target="_blank" rel="noreferrer noopener">
              Live
            </a>
          ) : null}
        </div>
      </div>

      {/* Demo modal */}
      {demoOpen && project.demo ? (
        <div
          className="pd-modal"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            // click outside closes
            if (e.target === e.currentTarget) setDemoOpen(false);
          }}
        >
          <div className="pd-panel">
            <button className="pd-close" onClick={() => setDemoOpen(false)} aria-label="Close">
              ✕
            </button>

            <video
              ref={videoRef}
              className="pd-video"
              src={project.demo}
              controls
              autoPlay
              playsInline
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
